import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { writeAuditLog } from '../utils/auditLog';
import {
  SquaresFour,
  Users,
  UserPlus,
  ClipboardText,
  Warning,
  Truck,
  UsersThree,
  Gear,
  ChartBar,
  Info,
  CreditCard,
  SignOut,
} from '@phosphor-icons/react';

const NAV = [
  { to: '/dashboard', label: 'Dashboard',    Icon: SquaresFour },
  { to: '/dashboard/patients',  label: 'Patients',     Icon: Users },
  { to: '/dashboard/candidates',label: 'Candidates',   Icon: UserPlus },
  { to: '/dashboard/activity',  label: 'Activity Log', Icon: ClipboardText },
  { to: '/dashboard/alerts',    label: 'Alerts',       Icon: Warning },
  { to: '/dashboard/deliveries',label: 'Deliveries',   Icon: Truck },
  { to: '/dashboard/team',      label: 'Team',         Icon: UsersThree },
  { to: '/dashboard/settings',  label: 'Settings',     Icon: Gear },
];

const TECH_ALLOWED = ['/dashboard', '/dashboard/alerts', '/dashboard/deliveries'];

export default function Layout({ children }) {
  const { pharmacy, role, user } = useAuth();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    if (pharmacy?.id && user) {
      await writeAuditLog(pharmacy.id, user, {
        action: 'Logout',
        entityType: 'auth',
        entityId: user.uid,
        entityName: user.email,
        actorRole: role || '',
        source: 'client',
      });
    }
    await signOut(auth);
    navigate('/login');
  };

  const visibleNav = NAV.filter(n =>
    role === 'superadmin' || role === 'admin' || role === 'pharmacist'
      ? true
      : TECH_ALLOWED.includes(n.to)
  );

  const linkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 11,
    padding: '9px 14px', margin: '1px 8px', borderRadius: 8,
    fontSize: 13.5, fontWeight: isActive ? 600 : 400, textDecoration: 'none',
    color: isActive ? '#fff' : '#7E96B0',
    background: isActive ? '#1768C4' : 'transparent',
    borderLeft: 'none',
    transition: 'all 0.15s',
  });

  const bottomLinkStyle = (isActive) => ({
    display: 'flex', alignItems: 'center', gap: 11,
    padding: '8px 14px', margin: '1px 8px', borderRadius: 8,
    fontSize: 12.5, textDecoration: 'none',
    color: isActive ? '#fff' : '#5A7090',
    background: isActive ? '#1E293B' : 'transparent',
    borderLeft: 'none',
  });

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Sidebar */}
      <nav style={{
        width: 230, minWidth: 230, background: '#0B1120', color: '#94A3B8',
        display: 'flex', flexDirection: 'column', padding: 0, overflowY: 'auto'
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid #1E293B' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="36" height="36">
              <rect x="90"  y="130" width="92" height="92" rx="20" fill="#1768C4"/>
              <rect x="210" y="130" width="92" height="92" rx="20" fill="#2D6FB7"/>
              <rect x="330" y="130" width="92" height="92" rx="20" fill="#4C9DDB"/>
              <rect x="90"  y="250" width="92" height="92" rx="20" fill="#4FC3D6"/>
              <rect x="210" y="250" width="92" height="92" rx="20" fill="#8EDCE6"/>
              <rect x="330" y="250" width="92" height="92" rx="20" fill="#CFEFF5"/>
            </svg>
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, lineHeight: 1 }}>
                <span style={{ color: '#fff' }}>Blister</span>
                <span style={{ color: '#4C9DDB' }}>Flow</span>
              </div>
              <div style={{ fontSize: 9, color: '#64748B', letterSpacing: '0.08em', marginTop: 2 }}>
                BLISTER PACKAGING

              </div>
            </div>
          </div>
          {pharmacy && (
            <div style={{ marginTop: 10, fontSize: 11, color: '#64748B' }}>
              <span style={{ color: '#CBD5E1' }}>{pharmacy.name}</span> · <span style={{ color: '#22C55E', fontWeight: 600 }}>active</span>
            </div>
          )}
        </div>

        {/* Nav links */}
        <div style={{ flex: 1, padding: '8px 0' }}>
          {visibleNav.map(({ to, label, Icon }) => (
            <NavLink key={to} to={to} end={to === '/dashboard'}
              style={({ isActive }) => linkStyle(isActive)}>
              <Icon size={18} weight="light" />
              {label}
            </NavLink>
          ))}
          {/* Reports — disabled */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 11,
            padding: '9px 14px', margin: '1px 8px', borderRadius: 8, fontSize: 13.5,
            color: '#2D3E52', borderLeft: 'none',
            cursor: 'default', userSelect: 'none',
          }}>
            <ChartBar size={18} weight="light" />
            Reports
            <span style={{ fontSize: 10, color: '#334155', marginLeft: 2 }}>soon</span>
          </div>
        </div>

        {/* Bottom links */}
        <div style={{ borderTop: '1px solid #1E293B', padding: '8px 0' }}>
          <NavLink to="/dashboard/account"
            style={({ isActive }) => bottomLinkStyle(isActive)}>
            <Info size={16} weight="light" />
            Account Info
          </NavLink>
          {(role === 'admin' || role === 'superadmin') && (
            <NavLink to="/dashboard/billing"
              style={({ isActive }) => bottomLinkStyle(isActive)}>
              <CreditCard size={16} weight="light" />
              Billing
            </NavLink>
          )}
          <button onClick={handleLogout} disabled={loggingOut}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              width: '100%', padding: '8px 14px', margin: '1px 8px', borderRadius: 8,
              width: 'calc(100% - 16px)', fontSize: 12.5,
              color: '#5A7090', background: 'none', border: 'none',
              cursor: 'pointer', textAlign: 'left',
              borderLeft: 'none',
            }}>
            <SignOut size={16} weight="light" />
            {loggingOut ? 'Logging out…' : 'Sign Out'}
          </button>
        </div>
      </nav>

      {/* Main content */}
      <main style={{ flex: 1, background: '#F8FAFC', overflowY: 'auto' }}>
        {children}
      </main>
    </div>
  );
}
